<template>
  <div class="atlas-ingestion-upload-panel">
    <section v-if="allGroupsUploadComplete" class="atlas-ingestion-upload-panel__section">
      <div class="atlas-ingestion-upload-panel__complete-banner" role="status">
        <span class="atlas-ingestion-upload-panel__complete-banner-icon" aria-hidden="true">
          <IconEmbedded name="check_4" :size="22" color="#2c7517" />
        </span>
        <div class="atlas-ingestion-upload-panel__complete-banner-text">
          <span class="atlas-ingestion-upload-panel__complete-banner-title">Upload complete!</span>
          <span class="atlas-ingestion-upload-panel__complete-banner-sub">You will be redirected automatically in a few seconds, or use the button below.</span>
        </div>
        <SrpButton class="atlas-ingestion-upload-panel__complete-banner-cta" size="small" color="teal" @click="returnToOriginAfterComplete"> Go Now </SrpButton>
      </div>
    </section>

    <section class="atlas-ingestion-upload-panel__section" :class="{ 'atlas-ingestion-upload-panel__section--merge-sticky': mergeMode }">
      <div v-if="!uploadingStarted && !mergeMode && !requiresFolderRebind" class="atlas-ingestion-upload-panel__ready-banner">
        <span class="atlas-ingestion-upload-panel__ready-banner-icon-wrap" aria-hidden="true">
          <span class="atlas-ingestion-upload-panel__ready-banner-icon">
            <IconEmbedded name="save_2" :size="22" color="#ffffff" />
          </span>
          <span class="atlas-ingestion-upload-panel__ready-banner-check-badge">
            <IconEmbedded name="check_4" :size="10" color="#ffffff" />
          </span>
        </span>
        <div class="atlas-ingestion-upload-panel__ready-banner-main">
          <div class="atlas-ingestion-upload-panel__ready-banner-text">
            <template v-if="unmatchedGroups.length > 0 && totalImagesToUpload === 0">
              <span class="atlas-ingestion-upload-panel__ready-banner-title-line atlas-ingestion-upload-panel__ready-banner-title-line--no-count">No photos queued for upload yet</span>
              <span class="atlas-ingestion-upload-panel__ready-banner-sub">
                {{ unmatchedFilesCount.toLocaleString() }} unmatched file(s) below — add them as a group to include them, or analyze again.
              </span>
            </template>
            <template v-else>
              <span class="atlas-ingestion-upload-panel__ready-banner-count">{{ totalImagesToUpload.toLocaleString() }}</span>
              <span class="atlas-ingestion-upload-panel__ready-banner-title-line">photos ready to upload</span>
              <span class="atlas-ingestion-upload-panel__ready-banner-sub">Across {{ groups.length }} groups</span>
            </template>
          </div>
          <template v-if="totalImagesToUpload > 0">
            <span class="atlas-ingestion-upload-panel__ready-banner-divider" aria-hidden="true" />
            <div class="atlas-ingestion-upload-panel__ready-banner-stat">
              <!-- <IconEmbedded class="atlas-ingestion-upload-panel__ready-banner-stat-icon" name="folder_2" :size="18" color="rgba(0, 0, 0, 0.55)" /> -->
              <span class="atlas-ingestion-upload-panel__ready-banner-stat-num">{{ groups.length }}</span>
              <span class="atlas-ingestion-upload-panel__ready-banner-stat-label">Upload groups</span>
            </div>
            <span class="atlas-ingestion-upload-panel__ready-banner-divider" aria-hidden="true" />
            <div class="atlas-ingestion-upload-panel__ready-banner-stat">
              <!-- <IconEmbedded class="atlas-ingestion-upload-panel__ready-banner-stat-icon" name="image_2" :size="18" color="rgba(0, 0, 0, 0.55)" /> -->
              <span class="atlas-ingestion-upload-panel__ready-banner-stat-num">{{ totalImagesToUpload.toLocaleString() }}</span>
              <span class="atlas-ingestion-upload-panel__ready-banner-stat-label">Total photos</span>
            </div>
          </template>
        </div>
        <SrpButton
          v-if="canMergeGroups"
          class="atlas-ingestion-upload-panel__ready-banner-merge-btn"
          size="small"
          color="gray"
          fill="outlined"
          :isDisabled="isAnalyzing || isUploading"
          @click="enterMergeSelectionMode"
        >
          <template #icon><IconEmbedded name="upload_2" :size="16" /></template>
          Merge groups
        </SrpButton>
      </div>
      <div v-else-if="mergeMode" class="atlas-ingestion-upload-panel__merge-banner">
        <span class="atlas-ingestion-upload-panel__merge-banner-icon" aria-hidden="true">
          <IconEmbedded name="merge_2" :size="22" color="#118689" />
        </span>
        <div class="atlas-ingestion-upload-panel__merge-banner-text">
          <span class="atlas-ingestion-upload-panel__merge-banner-title">Select groups to merge</span>
          <span class="atlas-ingestion-upload-panel__merge-banner-sub">{{ selectedGroupIds.length }} selected</span>
        </div>
        <div class="atlas-ingestion-upload-panel__merge-banner-actions">
          <SrpButton size="small" color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading" @click="cancelMergeMode"> Cancel </SrpButton>
          <SrpButton size="small" color="teal" :isDisabled="isAnalyzing || isUploading || selectedGroupIds.length < 2" @click="confirmMerge">
            Merge selected ({{ selectedGroupIds.length }})
          </SrpButton>
        </div>
      </div>
    </section>

    <section v-if="groups.length || unmatchedGroups.length > 0" class="atlas-ingestion-upload-panel__section">
      <template v-if="showLibraryProgressCard">
        <AtlasIngestionLibraryProgress
          :overallPercent="libraryOverallPercent"
          :currentGroupName="libraryCurrentGroupName"
          :currentGroupUploadedCount="libraryCurrentGroupUploadedCount"
          :currentGroupTotalCount="libraryCurrentGroupTotalCount"
          :uploadedImagesCount="libraryUploadedImagesCount"
          :totalImagesToUpload="totalImagesToUpload"
          :completedGroupsCount="libraryCompletedGroupsCount"
          :totalGroupsCount="libraryTotalGroupsCount"
          :secondsRemainingApprox="librarySecondsRemainingApprox"
        />
        <div class="atlas-ingestion-upload-panel__groups-heading-row atlas-ingestion-upload-panel__groups-heading-row--after-progress">
          <h4 class="global-h4 atlas-ingestion-upload-panel__group-progress-heading">Group progress</h4>
          <InfoIconWithTooltip v-if="analysisNotes" class="atlas-ingestion-upload-panel__analysis-notes-tooltip" :width="320" :iconSize="15">
            <span class="atlas-ingestion-upload-panel__tooltip-block">{{ analysisNotes }}</span>
          </InfoIconWithTooltip>
        </div>
      </template>
      <div v-else class="atlas-ingestion-upload-panel__groups-heading-row">
        <template v-if="showProposedGroupsHeading">
          <div class="atlas-ingestion-upload-panel__groups-heading-left">
            <h3 class="global-h4 atlas-ingestion-upload-panel__groups-heading-title">Proposed Upload Groups</h3>
            <!--<span class="atlas-ingestion-upload-panel__groups-heading-count-pill">{{ groups.length }}</span>-->
            <InfoIconWithTooltip v-if="analysisNotes" class="atlas-ingestion-upload-panel__analysis-notes-tooltip" :width="320" :iconSize="15">
              <span class="atlas-ingestion-upload-panel__tooltip-block">{{ analysisNotes }}</span>
            </InfoIconWithTooltip>
          </div>
          <p class="atlas-ingestion-upload-panel__groups-heading-helper">Review, rename, exclude, or merge groups before uploading.</p>
        </template>
        <template v-else>
          <h3 class="global-h4 atlas-ingestion-upload-panel__groups-heading-title">{{ groupsSectionHeading }}</h3>
          <InfoIconWithTooltip v-if="analysisNotes" class="atlas-ingestion-upload-panel__analysis-notes-tooltip" :width="320" :iconSize="15">
            <span class="atlas-ingestion-upload-panel__tooltip-block">{{ analysisNotes }}</span>
          </InfoIconWithTooltip>
        </template>
      </div>
      <AtlasIngestionGroupCard
        v-for="group in groups"
        :key="group.groupId"
        :group="group"
        :folderLabel="folderLabel"
        :showDebug="showDebug"
        :uploadingStarted="uploadingStarted"
        :isSelectable="mergeMode"
        :isSelected="isGroupSelected(group.groupId)"
        :isPreviewExpanded="expandedGroupsById[group.groupId] === true"
        @skip="emit('removeGroup', group.groupId)"
        @togglePreview="emit('togglePreview', group.groupId)"
        @toggleSelected="toggleGroupSelected(group.groupId)"
        @rename="emit('renameGroup', group.groupId, $event)"
        @removePrefixSource="(sourceType, sourceValue) => emit('removePrefixSource', group.groupId, sourceType, sourceValue)"
        @persist="emit('persist')"
      />
    </section>

    <section v-if="groups.length || unmatchedGroups.length > 0" class="atlas-ingestion-upload-panel__section atlas-ingestion-upload-panel__section--actions-floating">
      <NoteWithIcon v-if="limitErrorMessage" class="atlas-ingestion-upload-panel__limit-note" color="red" icon="warning_2" role="alert">
        <template #text>
          <p
            v-for="(line, i) in limitErrorMessage"
            :key="i"
            class="global-p atlas-ingestion-upload-panel__limit-note-line"
            :class="{ 'atlas-ingestion-upload-panel__limit-note-line--followup': i > 0 }"
          >
            {{ line }}
          </p>
        </template>
      </NoteWithIcon>
      <div
        v-if="requiresFolderRebind && !allGroupsUploadComplete"
        class="atlas-ingestion-upload-panel__notice atlas-ingestion-upload-panel__notice--warning atlas-ingestion-upload-panel__session-restored-notice"
      >
        Session restored from browser storage. Select the same folder again before uploading.
      </div>
      <div class="atlas-ingestion-upload-panel__actions">
        <div class="atlas-ingestion-upload-panel__actions-bar">
          <div class="atlas-ingestion-upload-panel__action-button-row">
            <SrpButton v-if="requiresFolderRebind && !allGroupsUploadComplete" :isDisabled="isAnalyzing || isUploading || mergeMode" @click="emit('selectFolder')">
              <template #icon><IconEmbedded name="folder_2" :size="17" /></template>
              Select Folder
            </SrpButton>
            <SrpButton v-else color="teal" :isDisabled="beginUploadButtonDisabled || mergeMode" @click="onBeginUploadClick">
              <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
              {{ beginUploadButtonLabel }}
            </SrpButton>
            <SrpButton color="gray" fill="outlined" :isDisabled="isAnalyzing || isUploading || mergeMode" @click="emit('back')">
              <template #icon><IconEmbedded name="arrow-left_2" :size="17" /></template>
              Back
            </SrpButton>
          </div>
          <div v-if="$slots.actionsTrailing" class="atlas-ingestion-upload-panel__actions-trailing">
            <slot name="actionsTrailing" />
          </div>
        </div>
      </div>
    </section>

    <section v-if="unmatchedGroups.length > 0 && !mergeMode" class="atlas-ingestion-upload-panel__section atlas-ingestion-upload-panel__section--unmatched">
      <div class="atlas-ingestion-upload-panel__unmatched-heading-row">
        <h4 class="global-h4 atlas-ingestion-upload-panel__unmatched-heading">Excluded from upload</h4>
      </div>
      <p class="global-p atlas-ingestion-upload-panel__unmatched-note">These {{ unmatchedFilesCount }} files are not currently marked for upload.</p>
      <AtlasIngestionGroupCard
        v-for="group in unmatchedGroups"
        :key="group.groupId"
        :group="group"
        :folderLabel="folderLabel"
        :showDebug="showDebug"
        :uploadingStarted="uploadingStarted"
        :isSelectable="false"
        :isSelected="false"
        :isPreviewExpanded="expandedGroupsById[group.groupId] === true"
        isUnmatched
        @togglePreview="emit('togglePreview', group.groupId)"
        @promote="emit('promoteUnmatched', group.groupId)"
        @persist="emit('persist')"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import AtlasIngestionGroupCard, { type AtlasIngestionEditableGroup } from "./AtlasIngestionGroupCard.vue";
import AtlasIngestionLibraryProgress from "./AtlasIngestionLibraryProgress.vue";
import { DEFAULT_BULK_UPLOAD_RETURN_TO, returnToRouteLocation, type BulkUploadReturnTo } from "./bulkUploadReturnTo";

defineOptions({
  name: "AtlasIngestionUploadPanel",
});

const AUTO_REDIRECT_AFTER_COMPLETE_MS = 3000;

const props = withDefaults(
  defineProps<{
    groups: Array<AtlasIngestionEditableGroup>;
    /** Groups shown separately and excluded from upload until promoted. */
    unmatchedGroups: Array<AtlasIngestionEditableGroup>;
    folderLabel: string;
    showDebug: boolean;
    analysisNotes: string | null;
    isAnalyzing: boolean;
    isUploading: boolean;
    uploadingStarted: boolean;
    requiresFolderRebind: boolean;
    allGroupsUploadComplete: boolean;
    beginUploadButtonDisabled: boolean;
    beginUploadButtonLabel: string;
    groupsSectionHeading: string;
    libraryOverallPercent: number;
    libraryUploadedImagesCount: number;
    libraryCurrentGroupName: string;
    libraryCurrentGroupUploadedCount: number;
    libraryCurrentGroupTotalCount: number;
    libraryCompletedGroupsCount: number;
    libraryTotalGroupsCount: number;
    librarySecondsRemainingApprox: number | null;
    /** When set, Begin Upload is disabled from the parent and this message explains why. */
    limitErrorMessage: ReadonlyArray<string> | null;
    expandedGroupsById: Record<string, boolean>;
    /** Where to send the user after a successful full upload (`atlas` = content calendar upload, `library` = photos). */
    returnTo?: BulkUploadReturnTo;
    customerId: string;
  }>(),
  {
    returnTo: DEFAULT_BULK_UPLOAD_RETURN_TO,
  }
);

const emit = defineEmits<{
  selectFolder: [];
  uploadAll: [];
  back: [];
  removeGroup: [groupId: string];
  removePrefixSource: [groupId: string, sourceType: "folderPrefix" | "filenamePrefix", sourceValue: string];
  togglePreview: [groupId: string];
  renameGroup: [groupId: string, name: string];
  mergeGroups: [groupIds: string[]];
  promoteUnmatched: [groupId: string];
  persist: [];
  beforeReturnNavigation: [];
}>();

const router = useRouter();

let autoRedirectTimerId: ReturnType<typeof setTimeout> | null = null;
const isReturnNavigationStarted = ref(false);

function clearAutoRedirectTimer() {
  if (autoRedirectTimerId !== null) {
    clearTimeout(autoRedirectTimerId);
    autoRedirectTimerId = null;
  }
}

function returnToOriginAfterComplete() {
  if (isReturnNavigationStarted.value) {
    return;
  }
  isReturnNavigationStarted.value = true;
  clearAutoRedirectTimer();
  emit("beforeReturnNavigation");
  void router.push(returnToRouteLocation(props.customerId, props.returnTo));
}

watch(
  () => props.allGroupsUploadComplete,
  complete => {
    if (!complete) {
      clearAutoRedirectTimer();
      isReturnNavigationStarted.value = false;
      return;
    }
    clearAutoRedirectTimer();
    autoRedirectTimerId = setTimeout(() => {
      autoRedirectTimerId = null;
      returnToOriginAfterComplete();
    }, AUTO_REDIRECT_AFTER_COMPLETE_MS);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearAutoRedirectTimer();
});

const mergeMode = ref(false);
const selectedGroupIds = ref<string[]>([]);

const totalImagesToUpload = computed(() => props.groups.reduce((sum, g) => sum + g.filePaths.length, 0));
const unmatchedFilesCount = computed(() => props.unmatchedGroups.reduce((sum, g) => sum + g.filePaths.length, 0));

const showLibraryProgressCard = computed(() => props.uploadingStarted && !props.allGroupsUploadComplete);

/** Pre-upload review: show split title + helper (not while library progress card is shown, not while actively uploading, not after all groups complete). */
const showProposedGroupsHeading = computed(() => !showLibraryProgressCard.value && !props.isUploading && !props.allGroupsUploadComplete);

const canMergeGroups = computed(() => !props.uploadingStarted && !props.requiresFolderRebind && props.groups.length >= 2 && !props.isAnalyzing && !props.isUploading);

function enterMergeSelectionMode() {
  mergeMode.value = true;
  selectedGroupIds.value = [];
}

function cancelMergeMode() {
  mergeMode.value = false;
  selectedGroupIds.value = [];
}

function isGroupSelected(groupId: string): boolean {
  return selectedGroupIds.value.includes(groupId);
}

function toggleGroupSelected(groupId: string) {
  const idx = selectedGroupIds.value.indexOf(groupId);
  if (idx >= 0) {
    selectedGroupIds.value = selectedGroupIds.value.filter(k => k !== groupId);
  } else {
    selectedGroupIds.value = [...selectedGroupIds.value, groupId];
  }
}

function confirmMerge() {
  if (selectedGroupIds.value.length < 2) {
    return;
  }
  emit("mergeGroups", [...selectedGroupIds.value]);
  cancelMergeMode();
}

function scrollToTopForUploadProgress() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function onBeginUploadClick() {
  emit("uploadAll");
  void nextTick(() => {
    scrollToTopForUploadProgress();
  });
}
</script>

<style scoped lang="scss">
.atlas-ingestion-upload-panel {
  &__section {
    margin-top: 20px;
    margin-bottom: 35px;
  }

  &__section--actions-floating {
    position: sticky;
    inset: auto auto 15px 0;
    z-index: 2;
    margin-top: 20px;
    padding-top: 6px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 -15px 20px 20px rgba(255, 255, 255, 1);
  }

  &__section--merge-sticky {
    position: sticky;
    inset: 15px 0 auto 0;
    z-index: 2;
    margin-top: 0;
    padding-bottom: 6px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 15px 20px 20px rgba(255, 255, 255, 1);
  }

  &__notice {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;

    &--warning {
      background: rgba(255, 193, 7, 0.16);
    }
  }

  &__session-restored-notice {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__session-restored-notice + &__actions {
    margin-top: 12px;
  }

  &__limit-note {
    margin-bottom: 12px;
  }

  &__limit-note-line {
    margin: 0;
    line-height: 1.45;
  }

  &__limit-note-line--followup {
    margin-top: 8px;
  }

  &__actions {
    margin-top: 20px;
    width: 100%;
  }

  &__actions-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  }

  &__action-button-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    flex: 0 1 auto;
  }

  &__actions-trailing {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;

    :deep(.super-user-action-panel) {
      width: auto;
    }
  }

  &__ready-banner {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 18px 20px;
    margin-bottom: 0;
    border-radius: 12px;
    background: #fafaf8;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
    flex-wrap: wrap;
  }

  &__ready-banner-icon-wrap {
    flex-shrink: 0;
    position: relative;
    width: 56px;
    height: 56px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(17, 134, 137, 0.22) 0%, rgba(17, 134, 137, 0.06) 62%, transparent 68%);
  }

  &__ready-banner-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #118689;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  &__ready-banner-check-badge {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #2c7517;
    border: 2px solid #fafaf8;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__ready-banner-main {
    flex: 0 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px 20px;
  }

  &__ready-banner-text {
    flex: 0 1 auto;
    min-width: 0;
    max-width: min(100%, 420px);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__ready-banner-count {
    font-weight: 700;
    font-size: 32px;
    line-height: 1.1;
    color: #1a1a1a;
    letter-spacing: -0.02em;
  }

  &__ready-banner-title-line {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.25;
    color: #1a1a1a;

    &--no-count {
      font-size: 16px;
      font-weight: 700;
    }
  }

  &__ready-banner-sub {
    font-size: 13px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #888);
    margin-top: 2px;
  }

  &__ready-banner-divider {
    flex-shrink: 0;
    width: 1px;
    height: 44px;
    background: rgba(0, 0, 0, 0.1);
    align-self: center;
  }

  &__ready-banner-stat {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 72px;
    text-align: left;
  }

  &__ready-banner-stat-icon {
    flex-shrink: 0;
    opacity: 0.85;
  }

  &__ready-banner-stat-num {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  &__ready-banner-stat-label {
    font-size: 11px;
    line-height: 1.2;
    color: var(--srp-color-text-muted, #888);
    max-width: 88px;
  }

  &__ready-banner-merge-btn {
    flex-shrink: 0;
    align-self: center;
    margin-left: auto;
  }

  &__complete-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    margin-bottom: 0;
    border-radius: 10px;
    background: rgb(206, 228, 200);
    flex-wrap: wrap;
  }

  &__complete-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(44, 117, 23, 0.12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__complete-banner-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__complete-banner-title {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1a1a1a;
  }

  &__complete-banner-sub {
    font-size: 14px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__complete-banner-cta {
    flex-shrink: 0;
    align-self: center;
  }

  &__merge-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    border-radius: 10px;
    background: rgb(206, 228, 200);
    flex-wrap: wrap;
  }

  &__merge-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__merge-banner-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__merge-banner-title {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
    color: #1a1a1a;
  }

  &__merge-banner-sub {
    font-size: 14px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__merge-banner-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__groups-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
    flex-wrap: wrap;
  }

  &__groups-heading-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 10px;
    min-width: 0;
  }

  &__groups-heading-title {
    margin: 0;
  }

  &__groups-heading-count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #495057;
    background: #e9ecef;
  }

  &__groups-heading-helper {
    margin: 0;
    flex: 1 1 220px;
    text-align: right;
    font-size: 13px;
    line-height: 1.45;
    color: var(--srp-color-text-muted, #888);
    min-width: 0;
  }

  &__groups-heading-row--after-progress {
    margin-top: 16px;
  }

  &__group-progress-heading {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
  }

  &__analysis-notes-tooltip {
    flex-shrink: 0;
  }

  &__tooltip-block {
    display: block;
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-wrap;
  }

  &__section--unmatched {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--srp-color-border-subtle, #dee2e6);
  }

  &__unmatched-heading-row {
    margin-bottom: 6px;
  }

  &__unmatched-heading {
    margin: 0;
    color: var(--srp-color-text-muted, #555);
  }

  &__unmatched-note {
    margin: 0 0 12px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--srp-color-text-muted, #666);
  }
}
</style>
